import React, { useCallback, useMemo } from "react";
import { compareWorkersByName, getWorkerDisplayLabel } from "../../../utils/business/worker";

/**
 * Generic multi-select list of workers (checkboxes only). Controlled; does not mutate workers.
 * Used inside WorkerSelectModal and BroadcastModal.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.workers - List of worker objects { id, first, last, worker_type, ... }. Not mutated.
 * @param {Array<number>} props.selectedWorkerIds - Currently selected worker IDs
 * @param {function(number[]): void} props.onChange - Called when selection changes; receives new array of selected IDs
 * @param {Array<number>} [props.disabledWorkerIds=[]] - Worker IDs that should be shown but disabled (grayed out)
 * @param {boolean} [props.byCategory=false] - Group checkboxes by worker type/category
 * @param {boolean} [props.showRoles=true] - Show worker type(s) next to name
 * @param {boolean} [props.multiCol=false] - If true, render the list in a responsive multi-column grid (single col when false)
 * @param {number} [props.maxColumns] Cap columns when `multiCol` is true (`2` = one or two cols).
 */
export default function WorkerMultiSelect({
  workers = [],
  selectedWorkerIds = [],
  onChange,
  disabledWorkerIds = [],
  byCategory = false,
  showRoles = true,
  multiCol = false,
  maxColumns,
}) {
  const disabledSet = useMemo(
    () => new Set(disabledWorkerIds),
    [disabledWorkerIds]
  );
  const isDisabled = useCallback(
    (workerId) => disabledSet.has(workerId),
    [disabledSet]
  );

  // Sort a copy: non-disabled first, then by first name. Do not mutate original workers.
  const sortedWorkers = useMemo(() => {
    const copy = [...workers];
    return copy.sort((a, b) => {
      const aDisabled = disabledSet.has(a.id);
      const bDisabled = disabledSet.has(b.id);
      if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;
      return compareWorkersByName(a, b);
    });
  }, [workers, disabledSet]);

  const categories = useMemo(() => {
    const cats = Array.from(
      new Set(
        workers.flatMap((worker) =>
          (worker.worker_type || []).map((type) => type.type)
        )
      )
    );
    if (workers.some((w) => !w.worker_type || w.worker_type.length === 0)) {
      cats.push("Role not Defined");
    }
    return cats;
  }, [workers]);

  const handleChecked = useCallback(
    (e) => {
      const workerId = Number(e.currentTarget.value);
      const checked = e.currentTarget.checked;
      const newIds = checked
        ? [...selectedWorkerIds, workerId]
        : selectedWorkerIds.filter((id) => id !== workerId);
      onChange(newIds);
    },
    [selectedWorkerIds, onChange]
  );

  const selectedSet = useMemo(
    () => new Set(selectedWorkerIds),
    [selectedWorkerIds]
  );

  /**
   * Renders one worker checkbox row, optionally with role and truncated name.
   *
   * @param {Object} worker Worker row.
   * @param {Object} [options] Row display options.
   * @param {boolean} [options.showRolesInItem] Whether to show role text.
   * @param {boolean} [options.truncateName=false] Whether to truncate the name.
   * @returns {JSX.Element} Checkbox list item.
   */
  const renderWorkerCheckbox = (worker, options = {}) => {
    const { showRolesInItem = showRoles, truncateName = false } = options;
    const disabled = isDisabled(worker.id);
    const workerLabel = getWorkerDisplayLabel(worker);
    const nameClasses = truncateName
      ? `ml-2 truncate min-w-0 ${disabled ? "text-gray-300" : "text-black"}`
      : `inline-block ml-2 text-nowrap ${disabled ? "text-gray-300" : "text-black"}`;
    return (
      <li key={worker.id} className={`text-sm ${truncateName ? "min-w-0" : ""}`}>
        <label
          className={`flex items-center justify-between ${truncateName ? "min-w-0" : ""}`}
        >
          <div
            className={`flex items-center ${truncateName ? "min-w-0 flex-1 overflow-hidden" : ""}`}
          >
            <input
              type="checkbox"
              name="worker"
              value={worker.id}
              disabled={disabled}
              checked={selectedSet.has(worker.id)}
              onChange={handleChecked}
              className={truncateName ? "flex-shrink-0" : ""}
            />
            <span className={nameClasses} title={truncateName ? workerLabel : undefined}>
              {workerLabel}
            </span>
          </div>
          {showRolesInItem && worker.worker_type?.length > 0 && (
            <span
              className={`ml-2 text-xs flex-shrink-0 ${disabled ? "text-gray-300" : "text-gray-500"}`}
            >
              {worker.worker_type.map((type) => type.type).join(", ")}
            </span>
          )}
        </label>
      </li>
    );
  };

  if (byCategory) {
    return (
      <div className="grid grid-cols-4 gap-4 mb-4">
        {categories.map((category) => (
          <div
            key={category}
            className="flex flex-col space-y-2 border-r border-solid border-gray-200 pr-4 last:border-0 last:pr-0"
          >
            <div className="flex flex-row justify-between text-md font-bold">
              <h2 className="text-gray-800 capitalize">{category}</h2>
            </div>
            <ul className="flex flex-col space-y-2 ml-2">
              {sortedWorkers
                .filter(
                  (worker) =>
                    (worker.worker_type || []).some(
                      (type) => type.type === category
                    ) ||
                    (category === "Role not Defined" &&
                      (!worker.worker_type || worker.worker_type.length === 0))
                )
                .map((worker) =>
                  renderWorkerCheckbox(worker, { showRolesInItem: false })
                )}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  const listClassName = !multiCol
    ? "flex flex-col space-y-2 ml-2"
    : maxColumns === 2
      ? "grid gap-x-4 gap-y-2 ml-2 w-full [grid-template-columns:repeat(auto-fit,minmax(max(18rem,calc(50%-0.5rem)),1fr))]"
      : "grid gap-x-4 gap-y-2 ml-2 w-full [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]";

  return (
    <div className={multiCol ? "mb-4 w-full min-h-0" : "grid grid-cols-1 gap-4 mb-4"}>
      <ul className={listClassName}>
        {sortedWorkers.map((worker) =>
          renderWorkerCheckbox(worker, { truncateName: multiCol })
        )}
      </ul>
    </div>
  );
}
